import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, Plus, X, Edit3, Trash2, Search, RefreshCw, Download,
  Store, ParkingSquare, Church, School, Building2, Users, Calendar,
  ShieldCheck, ShieldAlert, ShieldX, ClipboardCheck, Clock,
  MessageSquare, FileText, QrCode, AlertTriangle, Heart, MapPin, Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICON = {
  store: Store, parking_lot: ParkingSquare, church: Church, school: School,
  community_center: Users, event: Calendar, business: Building2, other: Building2,
};

const PERM_META = {
  permission_needed: { cls: 'bg-amber-100 text-amber-800 border-amber-200', Icon: ClipboardCheck },
  approved: { cls: 'bg-green-100 text-green-800 border-green-200', Icon: ShieldCheck },
  denied: { cls: 'bg-red-100 text-red-800 border-red-200', Icon: ShieldX },
  completed: { cls: 'bg-blue-100 text-blue-800 border-blue-200', Icon: ShieldCheck },
  follow_up_needed: { cls: 'bg-purple-100 text-purple-800 border-purple-200', Icon: Clock },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return '—'; }
};

/* =====================================================================
 * Location form modal
 * ===================================================================*/
const LocationFormModal = ({ open, initial, zones, lookups, onClose, onSave }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name || '',
        location_type: initial?.location_type || 'store',
        address: initial?.address || '',
        contact_name: initial?.contact_name || '',
        contact_email: initial?.contact_email || '',
        contact_phone: initial?.contact_phone || '',
        permission_status: initial?.permission_status || 'permission_needed',
        permission_contact_name: initial?.permission_contact_name || '',
        permission_contact_role: initial?.permission_contact_role || '',
        permission_date: initial?.permission_date || '',
        permission_notes: initial?.permission_notes || '',
        zone_id: initial?.zone_id || '',
        notes: initial?.notes || '',
      });
    }
  }, [open, initial]);

  if (!open || !form) return null;

  const isSensitive = (lookups.permission_sensitive_types || []).includes(form.location_type);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const body = { ...form, zone_id: form.zone_id || null };
      await onSave(body, initial?.id);
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="location-form-modal">
      <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-3">
        <div className="flex items-center justify-between sticky top-0 bg-white pb-2 border-b border-[#E5E7EB] -mx-5 -mt-5 px-5 pt-5">
          <div>
            <h3 className="font-heading text-base font-bold text-[#0A0A0A]">{isEdit ? 'Edit Location' : 'New Outreach Location'}</h3>
            <p className="text-[11px] text-[#4B5563] mt-0.5">
              Permission-first. Always confirm consent before flyering or setting up a table.
            </p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="location-form-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Location name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Walmart on Pitkin Ave"
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
              data-testid="location-name-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Type</label>
            <select value={form.location_type} onChange={(e) => setForm({ ...form, location_type: e.target.value })}
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
              data-testid="location-type-select">
              {(lookups.location_types || []).map(t => (
                <option key={t} value={t}>{lookups.location_type_labels?.[t] || t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Zone (optional)</label>
            <select value={form.zone_id} onChange={(e) => setForm({ ...form, zone_id: e.target.value })}
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
              data-testid="location-zone-select">
              <option value="">— No zone —</option>
              {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Address</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="500 Pitkin Ave, Brooklyn, NY"
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
              data-testid="location-address-input" />
          </div>
        </div>

        <div className="bg-[#FFF8F8] border border-[#BA1826]/20 rounded p-2.5 space-y-2">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#BA1826] flex items-center gap-1">
            <ClipboardCheck className="w-3 h-3" /> Permission &amp; consent
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Status</label>
              <select value={form.permission_status} onChange={(e) => setForm({ ...form, permission_status: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
                data-testid="location-permission-select">
                {(lookups.permission_statuses || []).map(p => (
                  <option key={p} value={p}>{lookups.permission_status_labels?.[p] || p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Permission date</label>
              <input type="date" value={form.permission_date}
                onChange={(e) => setForm({ ...form, permission_date: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
                data-testid="location-permission-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Who approved (name)</label>
              <input value={form.permission_contact_name} onChange={(e) => setForm({ ...form, permission_contact_name: e.target.value })}
                placeholder="Manager / pastor / event organizer"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
                data-testid="location-permission-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Their role</label>
              <input value={form.permission_contact_role} onChange={(e) => setForm({ ...form, permission_contact_role: e.target.value })}
                placeholder="Store Manager, Pastor, etc."
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
                data-testid="location-permission-role" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Permission notes</label>
              <textarea value={form.permission_notes} onChange={(e) => setForm({ ...form, permission_notes: e.target.value })}
                rows={2}
                placeholder="Any conditions they shared — best days/hours, area restrictions, partner contact details..."
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm resize-none"
                data-testid="location-permission-notes" />
            </div>
          </div>
          {isSensitive && (
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5"
              data-testid="permission-sensitive-banner">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>
                <strong>{lookups.location_type_labels?.[form.location_type] || 'Private property'}</strong> — please confirm verbal or written permission with a staff member before flyering or setting up a table. Always respect a "no".
              </span>
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">General contact (optional)</label>
            <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              placeholder="Day-to-day contact or main point person"
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
              data-testid="location-contact-name" />
          </div>
          <input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            placeholder="Contact email"
            className="px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
            data-testid="location-contact-email" />
          <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
            placeholder="Contact phone"
            className="px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
            data-testid="location-contact-phone" />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Notes</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            placeholder="Anything else the team should know about this location..."
            className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm resize-none"
            data-testid="location-notes-input" />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
          <button onClick={onClose} className="text-xs px-3 py-1.5 text-[#4B5563] hover:bg-[#F8F9FA] rounded">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim()}
            className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="location-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            {isEdit ? 'Save changes' : 'Create location'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* =====================================================================
 * Visit modal
 * ===================================================================*/
const VisitFormModal = ({ open, location, lookups, onClose, onSave }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        visit_date: new Date().toISOString().slice(0, 10),
        outreach_type: 'conversation_only',
        conversations_had: 0,
        flyers_shared: 0,
        qr_codes_shared: 0,
        table_setup_duration: '',
        partner_or_staff_present: '',
        estimated_foot_traffic: '',
        issues_or_complaints: '',
        notes: '',
        follow_up_needed: false,
        follow_up_date: '',
        donation_amount: '',
        volunteer_label: '',
        logged_on_behalf_of: '',
      });
    }
  }, [open]);

  if (!open || !location || !form) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        conversations_had: parseInt(form.conversations_had || 0),
        flyers_shared: parseInt(form.flyers_shared || 0),
        qr_codes_shared: parseInt(form.qr_codes_shared || 0),
        estimated_foot_traffic: form.estimated_foot_traffic === '' ? null : parseInt(form.estimated_foot_traffic),
        donation_amount: form.donation_amount === '' ? null : parseFloat(form.donation_amount),
      };
      await onSave(location.id, body);
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="visit-form-modal">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 space-y-3">
        <div className="flex items-center justify-between sticky top-0 bg-white pb-2 border-b border-[#E5E7EB] -mx-5 -mt-5 px-5 pt-5">
          <div>
            <h3 className="font-heading text-base font-bold text-[#0A0A0A] flex items-center gap-1.5">
              <Heart className="w-4 h-4 text-[#BA1826]" /> Log visit — {location.name}
            </h3>
            <p className="text-[11px] text-[#4B5563]">
              Conversations matter most. Counts come second. Donations come last.
            </p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="visit-form-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {location.permission_status === 'permission_needed' && (
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5"
            data-testid="visit-permission-banner">
            <ClipboardCheck className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>This location is still <b>Permission Needed</b>. Consider confirming consent before recording a visit — and update the location status when you do.</span>
          </p>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Visit date</label>
            <input type="date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })}
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
              data-testid="visit-date-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Outreach type</label>
            <select value={form.outreach_type} onChange={(e) => setForm({ ...form, outreach_type: e.target.value })}
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
              data-testid="visit-outreach-type">
              {(lookups.outreach_types || []).map(t => (
                <option key={t} value={t}>{lookups.outreach_type_labels?.[t] || t}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Conversations</label>
            <input type="number" min={0} value={form.conversations_had}
              onChange={(e) => setForm({ ...form, conversations_had: e.target.value })}
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
              data-testid="visit-conversations-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Flyers</label>
            <input type="number" min={0} value={form.flyers_shared}
              onChange={(e) => setForm({ ...form, flyers_shared: e.target.value })}
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
              data-testid="visit-flyers-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">QRs</label>
            <input type="number" min={0} value={form.qr_codes_shared}
              onChange={(e) => setForm({ ...form, qr_codes_shared: e.target.value })}
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
              data-testid="visit-qrs-input" />
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Notes</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            placeholder="What went well? Anything memorable from the conversations?"
            className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm resize-none"
            data-testid="visit-notes-input" />
        </div>

        <details className="border border-[#E5E7EB] rounded">
          <summary className="text-[11px] font-semibold text-[#4B5563] px-3 py-2 cursor-pointer">Site logistics &amp; follow-up (optional)</summary>
          <div className="p-3 space-y-2 border-t border-[#E5E7EB]">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Table setup duration</label>
                <input value={form.table_setup_duration}
                  onChange={(e) => setForm({ ...form, table_setup_duration: e.target.value })}
                  placeholder="e.g. 2h 30m"
                  className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
                  data-testid="visit-duration-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Foot traffic (est.)</label>
                <input type="number" min={0} value={form.estimated_foot_traffic}
                  onChange={(e) => setForm({ ...form, estimated_foot_traffic: e.target.value })}
                  placeholder="People who passed by"
                  className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
                  data-testid="visit-foot-traffic-input" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Partner / staff present</label>
              <input value={form.partner_or_staff_present}
                onChange={(e) => setForm({ ...form, partner_or_staff_present: e.target.value })}
                placeholder="Names of anyone joining you (partner org, store staff, etc.)"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
                data-testid="visit-partner-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Issues or complaints</label>
              <textarea value={form.issues_or_complaints}
                onChange={(e) => setForm({ ...form, issues_or_complaints: e.target.value })}
                rows={2}
                placeholder="Anything concerning that happened? Anyone asked us to leave?"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm resize-none"
                data-testid="visit-issues-input" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-[#0A0A0A]">
              <input type="checkbox" checked={form.follow_up_needed}
                onChange={(e) => setForm({ ...form, follow_up_needed: e.target.checked })}
                data-testid="visit-followup-checkbox" />
              <span>Follow-up needed</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Follow-up date</label>
                <input type="date" value={form.follow_up_date}
                  onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                  className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
                  data-testid="visit-followup-date" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Donation amount ($)</label>
                <input type="number" min={0} step="0.01" value={form.donation_amount}
                  onChange={(e) => setForm({ ...form, donation_amount: e.target.value })}
                  placeholder="Only if someone gave on the spot"
                  className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
                  data-testid="visit-donation-input" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Volunteer label / on behalf of</label>
              <input value={form.volunteer_label}
                onChange={(e) => setForm({ ...form, volunteer_label: e.target.value })}
                placeholder="e.g. Saturday Crew — East NY"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm"
                data-testid="visit-volunteer-label" />
            </div>
          </div>
        </details>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
          <button onClick={onClose} className="text-xs px-3 py-1.5 text-[#4B5563] hover:bg-[#F8F9FA] rounded">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="visit-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Heart className="w-3 h-3" />}
            Save visit
          </button>
        </div>
      </div>
    </div>
  );
};

/* =====================================================================
 * Location detail drawer (shows visits + totals)
 * ===================================================================*/
const LocationDetailDrawer = ({ open, locationId, onClose, onVisitChanged, onNewVisit, canWrite }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/outreach/locations/${locationId}/visits`,
        { withCredentials: true });
      setData(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [locationId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const deleteVisit = async (vid) => {
    if (!window.confirm('Remove this visit?')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/outreach/visits/${vid}`, { withCredentials: true });
    await load();
    onVisitChanged && onVisitChanged();
  };

  if (!open) return null;
  const loc = data?.location;
  const totals = data?.totals || {};
  const insightsOrder = data?.insights_order || ['conversations_had', 'flyers_shared', 'qr_codes_shared', 'follow_ups_pending', 'donations_total'];
  const totalLabels = {
    conversations_had: 'Conversations',
    flyers_shared: 'Flyers shared',
    qr_codes_shared: 'QRs shared',
    follow_ups_pending: 'Follow-ups',
    donations_total: 'Donations',
  };
  const totalIcons = {
    conversations_had: MessageSquare,
    flyers_shared: FileText,
    qr_codes_shared: QrCode,
    follow_ups_pending: Clock,
    donations_total: Heart,
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex" data-testid="location-detail-drawer">
      <div className="flex-1" onClick={onClose} />
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-xl flex flex-col">
        <div className="px-4 py-3 border-b border-[#E5E7EB] sticky top-0 bg-white z-10 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Location</p>
            <h3 className="font-heading text-base font-bold text-[#0A0A0A]">{loc?.name || '…'}</h3>
            {loc && (
              <p className="text-[11px] text-[#4B5563] flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {loc.address || 'No address set'}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="location-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 p-4 space-y-3">
          {loading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>}
          {loc && (
            <>
              {/* Permission banner */}
              <div className={`rounded p-2.5 text-xs border ${PERM_META[loc.permission_status]?.cls || ''}`}
                data-testid="location-detail-permission">
                <p className="font-bold flex items-center gap-1.5">
                  {React.createElement(PERM_META[loc.permission_status]?.Icon || ClipboardCheck, { className: 'w-3 h-3' })}
                  Permission: {loc.permission_status?.replace(/_/g, ' ')}
                </p>
                {loc.permission_contact_name && (
                  <p className="mt-1 text-[11px]">
                    Approved by <b>{loc.permission_contact_name}</b>
                    {loc.permission_contact_role ? ` (${loc.permission_contact_role})` : ''}
                    {loc.permission_date ? ` · ${loc.permission_date}` : ''}
                  </p>
                )}
                {loc.permission_notes && <p className="mt-1 text-[11px] italic">"{loc.permission_notes}"</p>}
              </div>

              {/* Totals (doctrine ordered) */}
              <div className="grid grid-cols-5 gap-1.5" data-testid="location-detail-totals">
                {insightsOrder.map((k) => {
                  const Ico = totalIcons[k] || MessageSquare;
                  const isDon = k === 'donations_total';
                  const val = totals[k] || 0;
                  return (
                    <div key={k}
                      className={`border rounded p-2 ${isDon ? 'border-green-200 bg-green-50' : 'border-[#E5E7EB] bg-white'}`}
                      data-testid={`location-total-${k}`}>
                      <Ico className={`w-3 h-3 ${isDon ? 'text-green-700' : 'text-[#BA1826]'}`} />
                      <p className={`text-base font-bold mt-0.5 ${isDon ? 'text-green-700' : 'text-[#0A0A0A]'}`}>
                        {isDon ? `$${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : val}
                      </p>
                      <p className="text-[9px] uppercase tracking-wider text-[#4B5563]">{totalLabels[k]}</p>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Visits ({data.visits.length})</p>
                {canWrite && loc.permission_status !== 'denied' && (
                  <button onClick={() => onNewVisit(loc)}
                    className="text-[11px] px-2 py-1 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] inline-flex items-center gap-1"
                    data-testid="location-detail-new-visit">
                    <Plus className="w-3 h-3" /> Log visit
                  </button>
                )}
              </div>

              {data.visits.length === 0 ? (
                <p className="text-xs text-[#9CA3AF] text-center py-4">No visits yet.</p>
              ) : (
                <ul className="space-y-1.5" data-testid="location-detail-visits">
                  {data.visits.map((v) => (
                    <li key={v.id} className="bg-white border border-[#E5E7EB] rounded p-2 text-xs"
                      data-testid={`visit-row-${v.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="font-semibold text-[#0A0A0A]">
                            {v.visit_date}
                            <span className="text-[#9CA3AF] font-normal"> · {data.outreach_type_labels?.[v.outreach_type] || v.outreach_type}</span>
                          </p>
                          <p className="text-[10px] text-[#4B5563] mt-0.5">
                            {v.conversations_had || 0} conv · {v.flyers_shared || 0} flyers · {v.qr_codes_shared || 0} QRs
                            {v.follow_up_needed && <span className="ml-1 text-amber-700 font-semibold">· follow-up</span>}
                          </p>
                          {v.notes && <p className="text-[11px] text-[#4B5563] mt-1">{v.notes}</p>}
                        </div>
                        {canWrite && (
                          <button onClick={() => deleteVisit(v.id)} className="text-red-600 hover:text-red-800"
                            data-testid={`visit-delete-${v.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* =====================================================================
 * Main Locations pane (placed inside AdminOutreachTab)
 * ===================================================================*/
const LocationsPane = ({ canWrite, zones }) => {
  const [data, setData] = useState({ locations: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ search: '', permission_status: '', location_type: '' });

  const [showForm, setShowForm] = useState(false);
  const [editingLoc, setEditingLoc] = useState(null);
  const [showVisitForm, setShowVisitForm] = useState(false);
  const [visitFor, setVisitFor] = useState(null);
  const [detailLocId, setDetailLocId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await axios.get(`${BACKEND_URL}/api/admin/outreach/locations?${params}`,
        { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load locations');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const saveLocation = async (body, id) => {
    if (id) await axios.put(`${BACKEND_URL}/api/admin/outreach/locations/${id}`, body, { withCredentials: true });
    else await axios.post(`${BACKEND_URL}/api/admin/outreach/locations`, body, { withCredentials: true });
    await load();
  };

  const deleteLocation = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"? This removes all its visit logs too.`)) return;
    await axios.delete(`${BACKEND_URL}/api/admin/outreach/locations/${loc.id}`, { withCredentials: true });
    await load();
  };

  const saveVisit = async (locId, body) => {
    await axios.post(`${BACKEND_URL}/api/admin/outreach/locations/${locId}/visits`, body, { withCredentials: true });
    await load();
  };

  const exportCsv = () => {
    window.location.href = `${BACKEND_URL}/api/admin/outreach/locations/export.csv`;
  };

  return (
    <div className="space-y-3" data-testid="outreach-locations-pane">
      <div className="bg-[#FFF8F8] border border-[#BA1826]/20 rounded p-2.5 text-[11px] text-[#4B5563] flex items-start gap-2">
        <ClipboardCheck className="w-3.5 h-3.5 text-[#BA1826] mt-0.5 flex-shrink-0" />
        <span>
          <strong>Permission-first outreach.</strong> For stores, parking lots, churches, schools and businesses, always confirm consent with a staff member before flyering or setting up a table — and respect a "no". Update the permission status here so the team always knows where we stand.
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input type="text" placeholder="Search name, address, contact..." value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="w-full pl-8 pr-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
            data-testid="locations-search" />
        </div>
        <select value={filters.permission_status}
          onChange={(e) => setFilters({ ...filters, permission_status: e.target.value })}
          className="px-2 py-2 border border-[#E5E7EB] rounded text-xs"
          data-testid="locations-permission-filter">
          <option value="">All statuses</option>
          {(data.permission_statuses || []).map(p => (
            <option key={p} value={p}>{data.permission_status_labels?.[p] || p}</option>
          ))}
        </select>
        <select value={filters.location_type}
          onChange={(e) => setFilters({ ...filters, location_type: e.target.value })}
          className="px-2 py-2 border border-[#E5E7EB] rounded text-xs"
          data-testid="locations-type-filter">
          <option value="">All types</option>
          {(data.location_types || []).map(t => (
            <option key={t} value={t}>{data.location_type_labels?.[t] || t}</option>
          ))}
        </select>
        <button onClick={load} disabled={loading}
          className="text-[11px] px-2 py-2 text-[#4B5563] hover:text-[#0A0A0A] inline-flex items-center gap-1"
          data-testid="locations-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <button onClick={exportCsv}
          className="text-[11px] px-2 py-2 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA] inline-flex items-center gap-1"
          data-testid="locations-export">
          <Download className="w-3 h-3" /> CSV
        </button>
        {canWrite && (
          <button onClick={() => { setEditingLoc(null); setShowForm(true); }}
            className="text-[11px] px-3 py-2 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] inline-flex items-center gap-1"
            data-testid="locations-new-btn">
            <Plus className="w-3 h-3" /> New location
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>
      ) : data.locations.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center" data-testid="locations-empty">
          <Store className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
          <p className="text-sm text-[#4B5563]">No locations yet. Add the first store, church, or community center your team plans to visit.</p>
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="locations-list">
          {data.locations.map((loc) => {
            const Ico = TYPE_ICON[loc.location_type] || Building2;
            const pm = PERM_META[loc.permission_status] || PERM_META.permission_needed;
            const PIco = pm.Icon;
            return (
              <li key={loc.id}
                className="bg-white border border-[#E5E7EB] rounded-lg p-3 flex items-start gap-3"
                data-testid={`location-card-${loc.id}`}>
                <Ico className="w-4 h-4 text-[#BA1826] flex-shrink-0 mt-0.5" />
                <button onClick={() => setDetailLocId(loc.id)} className="flex-1 min-w-0 text-left"
                  data-testid={`location-open-${loc.id}`}>
                  <p className="font-bold text-sm text-[#0A0A0A] inline-flex items-center gap-2 flex-wrap">
                    {loc.name}
                    <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">
                      {data.location_type_labels?.[loc.location_type] || loc.location_type}
                    </span>
                  </p>
                  <p className="text-[11px] text-[#4B5563]">
                    {loc.address ? <><MapPin className="inline w-3 h-3 mr-0.5" />{loc.address}</> : 'No address'}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${pm.cls}`}
                      data-testid={`location-perm-${loc.id}`}>
                      <PIco className="w-3 h-3" />
                      {data.permission_status_labels?.[loc.permission_status] || loc.permission_status}
                    </span>
                    <span className="text-[10px] text-[#9CA3AF]">
                      {loc.visit_count || 0} visit{loc.visit_count === 1 ? '' : 's'}
                      {loc.last_visit ? ` · last ${fmtDate(loc.last_visit)}` : ''}
                    </span>
                    <span className="text-[10px] text-[#9CA3AF]">
                      {loc.totals?.conversations_had || 0} conv · {loc.totals?.flyers_shared || 0} flyers · {loc.totals?.qr_codes_shared || 0} QRs
                    </span>
                  </div>
                </button>
                {canWrite && (
                  <div className="flex items-center gap-1">
                    {loc.permission_status !== 'denied' && (
                      <button onClick={() => { setVisitFor(loc); setShowVisitForm(true); }}
                        className="p-1.5 text-[#BA1826] hover:bg-[#FFF8F8] rounded"
                        title="Log visit" data-testid={`location-visit-${loc.id}`}>
                        <Heart className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => { setEditingLoc(loc); setShowForm(true); }}
                      className="p-1.5 text-[#4B5563] hover:text-[#0A0A0A] hover:bg-[#F8F9FA] rounded"
                      data-testid={`location-edit-${loc.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteLocation(loc)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      data-testid={`location-delete-${loc.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <LocationFormModal open={showForm} initial={editingLoc} zones={zones} lookups={data}
        onClose={() => { setShowForm(false); setEditingLoc(null); }}
        onSave={saveLocation} />
      <VisitFormModal open={showVisitForm} location={visitFor} lookups={data}
        onClose={() => { setShowVisitForm(false); setVisitFor(null); }}
        onSave={saveVisit} />
      <LocationDetailDrawer open={!!detailLocId} locationId={detailLocId}
        canWrite={canWrite}
        onClose={() => setDetailLocId(null)}
        onVisitChanged={load}
        onNewVisit={(loc) => { setVisitFor(loc); setShowVisitForm(true); }} />
    </div>
  );
};

export default LocationsPane;
